import React, {createRef, useContext, useEffect, useRef, useState} from "react";
import "./AddPhotos.scss";
import imgPlaceholder from "../../../assets/images/icon-placeholder.svg";
import Button from "../../shared/Button";
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import {useTranslation} from "react-i18next";
import uniqid from "uniqid";
import {MainFeedbackContext} from "../LocationFeedback";
import {getPhotosSize, initPhotos} from "../../../util/photos";
import {Message, Dimmer, Loader} from "semantic-ui-react";
import {getConfigData} from "../../../middleware";

const MAX_PHOTOS_UPLOAD_SIZE = getConfigData().maxFileSizeForUpload;
const MAX_CONCURRENT_UPLOADS = 3;

const AddPhotos = ({dataKey, isFeedback, isEdit, handleCta, nextStep, isBI}) => {
  const {review, setReview} = useContext(MainFeedbackContext);
  const _init = initPhotos(review.photos);
  const [photosSize, setPhotosSize] = useState(getPhotosSize(_init.photos));
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState("");
  const [photos = [], setPhotos] = useState(_init.photos);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const inputOpenFileRef = createRef();

  const {t} = useTranslation();
  const tr = t('photos', {returnObjects: true});
  const trCommon = t('common', {returnObjects: true});

  const continueCTA = isBI() ? tr.buttons.next_bi : tr.buttons.next;

  const fieldRef = useRef(null);
  const uploadQueue = useRef([]);
  const activeUploads = useRef(0);

  // Generate a unique key for this session, appending to dataKey (agentId) if present
  const dataKeyRefName = dataKey ? `${dataKey}_${uniqid()}` : uniqid();
  const photosKey = useRef(dataKeyRefName).current;
  
  const _config = getConfigData()[getConfigData().env];
  const cdnUri = _config.cdn.scheme + "://" + _config.cdn.host.replace(/\/$/, '');

  useEffect(() => {
    if (fieldRef.current) {
      fieldRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, []);

  const processUploadQueue = async () => {
    if (activeUploads.current >= MAX_CONCURRENT_UPLOADS || uploadQueue.current.length === 0) {
      if (uploadQueue.current.length === 0 && activeUploads.current === 0) {
        setIsUploading(false);
      }
      return;
    }

    const fileData = uploadQueue.current.shift();
    activeUploads.current++;

    try {
      await uploadFileToS3(fileData.file, fileData.targetIndex);
    } catch (error) {
      console.error(`Upload failed for file ${fileData.file.name}:`, error);
      setError(`Upload failed for ${fileData.file.name}. Please try again.`);
      setShowError(true);
    } finally {
      activeUploads.current--;
      setTimeout(processUploadQueue, 100);
    }
  };

  const uploadFileToS3 = async (file, targetIndex) => {
    try {
      setUploadProgress(prev => ({
        ...prev,
        [targetIndex]: { status: 'getting-url', progress: 0 }
      }));

      const presignedUrlResponse = await getPresignedUrl(file.name, photosKey);
      const { presignedUrl, s3Key } = presignedUrlResponse;

      setUploadProgress(prev => ({
        ...prev,
        [targetIndex]: { status: 'uploading', progress: 0 }
      }));

      await uploadToS3WithProgress(file, presignedUrl, targetIndex);

      setUploadProgress(prev => ({
        ...prev,
        [targetIndex]: { status: 'complete', progress: 100 }
      }));

      const finalImageUrl = `${cdnUri}/uploads/${photosKey}/${file.name}`;

      setPhotos(prevPhotos => {
        const newPhotos = [...prevPhotos];
        newPhotos[targetIndex] = {
          src: finalImageUrl,
          pristine: false,
          size: file.size,
          s3Key: s3Key,
          isLocal: false // No longer a local preview
        };
        return newPhotos;
      });

    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress(prev => ({
        ...prev,
        [targetIndex]: { status: 'error', progress: 0 }
      }));
      throw error;
    }
  };

  const getPresignedUrl = async (fileName, photosKey) => {
    const config = getConfigData()[getConfigData().env].tempUploads;
    const url = `${config.schema}://${config.host}/${config.context}/${config.path}?fileName=${encodeURIComponent(fileName)}&key=${photosKey}`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to get presigned URL');
    }
    
    return {
      presignedUrl: data.fileUploadURL,
      s3Key: data.s3Key
    };
  };

  const uploadToS3WithProgress = (file, presignedUrl, index) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          setUploadProgress(prev => ({
            ...prev,
            [index]: { status: 'uploading', progress: Math.round(progress) }
          }));
        }
      });
      
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.response);
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
        }
      });
      
      xhr.addEventListener("error", () => {
        reject(new Error(`Upload error: ${xhr.statusText}`));
      });
      
      xhr.open("PUT", presignedUrl);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });
  };

  const handleFileChange = async (e) => {
    if (e.target.files) {
      setIsUploading(true);
      setError("");
      setShowError(false);
      
      const files = Array.from(e.target.files);
      
      const validFiles = files.filter(file => {
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!validTypes.includes(file.type)) {
          setError(`Invalid file type: ${file.name}. Only JPEG and PNG files are allowed.`);
          setShowError(true);
          return false;
        }
        return true;
      });
      
      if (validFiles.length === 0) {
        setIsUploading(false);
        return;
      }
      
      let newSize = photosSize;
      const validFilesForUpload = [];
      
      for (const file of validFiles) {
        if ((file.size + newSize) <= (MAX_PHOTOS_UPLOAD_SIZE)) {
          validFilesForUpload.push(file);
          newSize += file.size;
        } else {
          const mbsize = ((file.size / 1024) / 1024).toFixed(2);
          const mbmax = ((MAX_PHOTOS_UPLOAD_SIZE / 1024) / 1024);
          setError(`Max size allowed (combined) is ${mbmax}MB. File '${file.name}' (${mbsize}MB) cannot be uploaded.`);
          setShowError(true);
        }
      }
      
      if (validFilesForUpload.length > 0) {
        setPhotosSize(newSize);

        const newUploadQueue = [];
        let currentPhotosCopy = [...photos];

        for (const file of validFilesForUpload) {
          const emptySlotIndex = currentPhotosCopy.findIndex(photo => photo.pristine);
          if (emptySlotIndex !== -1) {
            // Create local preview
            const blobUrl = URL.createObjectURL(file);
            
            newUploadQueue.push({
              file,
              targetIndex: emptySlotIndex
            });
            
            // Show local preview immediately
            currentPhotosCopy[emptySlotIndex] = {
              ...currentPhotosCopy[emptySlotIndex], 
              src: blobUrl,
              pristine: false,
              isLocal: true
            };
          }
        }

        setPhotos(currentPhotosCopy);
        uploadQueue.current = newUploadQueue;
        processUploadQueue();
      } else {
        setIsUploading(false);
      }
    }
  };

  const showOpenFileDlg = () => {
    closeError();
    inputOpenFileRef.current.click();
  };

  const saveAndContinue = () => {
    setReview({
      type: "SAVE_PHOTOS",
      data: {
        photos: photos.filter(photo => !photo.pristine),
        key: photosKey
      }
    });
    
    if (typeof handleCta === 'function') {
      handleCta();
    }
    if (typeof nextStep === 'function') {
      nextStep();
    }
  };

  const closeError = () => {
    setShowError(false);
    setError("");
  };

  const removePhoto = (index) => {
    setPhotos(prevPhotos => {
      const newPhotos = [...prevPhotos];
      setPhotosSize(photosSize - newPhotos[index].size);
      newPhotos[index] = {src: imgPlaceholder, pristine: true, size: 0};
      return newPhotos;
    });
  };

  const pristineCount = photos.filter(photo => photo.pristine).length;

  return (
    <div className="add-photos" ref={fieldRef}>
      {isUploading && (
        <Dimmer active inverted>
          <Loader size='large'>Uploading photos...</Loader>
        </Dimmer>
      )}
      
      {isFeedback && (
        <p className="instructions">
          {tr.subtitle}
        </p>
      )}
      
      {isFeedback && (
        <div key={uniqid()}>
          <Button 
            cta={tr.buttons.camera} 
            button_type="secondary" 
            handleClick={showOpenFileDlg}
            isDisabled={pristineCount === 0}
          />
          <input 
            ref={inputOpenFileRef} 
            onChange={handleFileChange} 
            type="file" 
            style={{display: "none"}}
            multiple 
            accept="image/jpeg, image/png"
            disabled={isUploading || pristineCount === 0}
          />
        </div>
      )}

      {showError && (
        <Message
          onDismiss={() => closeError()}
          error
          header='Error'
          content={error}
        />
      )}
      
      <div className="grid">
        {photos.map((photo, index) => {
          const photoClassName = photo.pristine ? "pristine" : "";
          const uploadStatus = uploadProgress[index];
          
          return (
            <div 
              className="Mask" 
              key={`${index}-${photo.src}`}
              onClick={photo.pristine ? showOpenFileDlg : undefined} 
              style={photo.pristine ? {cursor: pristineCount === 0 ? 'not-allowed' : 'pointer'} : {}}
            >
              <img 
                src={photo.src} 
                className={photoClassName} 
                alt={photo.pristine ? "Add photo" : "Uploaded photo"} 
                style={uploadStatus && uploadStatus.status !== 'complete' && !photo.pristine && !photo.isLocal ? {opacity: 0.5} : {}}
              />
              
              {!photo.pristine && (
                <div className="overlay">
                  <CloseOutlinedIcon 
                    className="icon" 
                    onClick={(e) => {
                      e.stopPropagation();
                      removePhoto(index);
                    }}
                  />
                </div>
              )}
              
              {uploadStatus && (
                <div className="upload-progress">
                  <div className="progress-status">{uploadStatus.status}</div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{width: `${uploadStatus.progress}%`}}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {isFeedback && (
        <div className="action-box">
          <Button
            cta={isEdit ? trCommon.save : continueCTA}
            button_type="primary"
            handleClick={() => {
              saveAndContinue();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AddPhotos;